import helpers from "@/helpers/global";

/**
 * Encode an assessment doc id into the `u_id` query parameter. This is the
 * exact encoding the results links have always used (`btoa` of the
 * URI-encoded id — see the cloud function and `getPagination`), so anything
 * built here round-trips through `decodeAssessmentResultsUserId`.
 */
export function encodeAssessmentResultsUserId(assessmentId: string): string {
  return btoa(encodeURIComponent(String(assessmentId || "")));
}

/**
 * Decode the u_id parameter of /assessment-results links (base64 + URI
 * component encoding). Tolerates the case where `+` in the base64 payload
 * was rewritten to a space by query-string parsing.
 */
export function decodeAssessmentResultsUserId(
  encodedUserId: unknown
): string | null {
  let raw = String(encodedUserId ?? "").trim();
  if (!raw) {
    return null;
  }
  try {
    raw = raw.replace(/ /g, "+");
    return decodeURIComponent(atob(raw));
  } catch (error) {
    console.error("Invalid assessment results user id:", error);
    return null;
  }
}

/**
 * Build the public candidate-results path using the route's actual contract:
 * `as_id` is the raw form id, while `u_id` is the encoded candidate id.
 */
export function buildAssessmentResultsPath(
  assessmentId: string,
  candidateId: string
): string {
  const normalizedAssessmentId = String(assessmentId ?? "").trim();
  const normalizedCandidateId = String(candidateId ?? "").trim();
  const encodedCandidateId = btoa(encodeURIComponent(normalizedCandidateId));

  return `/assessment-results?as_id=${encodeURIComponent(
    normalizedAssessmentId
  )}&u_id=${encodeURIComponent(encodedCandidateId)}`;
}

/**
 * Canonical Firestore assessments doc id: `{formId}-{emailKey}`.
 * Tolerates links built from raw email casing (e.g. Rosie@… vs rosie@…).
 */
export function normalizeAssessmentDocumentId(decodedId: string): string {
  const trimmed = String(decodedId ?? "").trim();
  if (!trimmed) {
    return trimmed;
  }
  const dash = trimmed.indexOf("-");
  if (dash <= 0) {
    return trimmed;
  }
  const formId = trimmed.slice(0, dash);
  const suffix = trimmed.slice(dash + 1);
  if (!suffix) {
    return trimmed;
  }
  if (suffix.includes("@")) {
    const emailGuess = suffix.replace(/\|\|/g, ".");
    return `${formId}-${helpers.emailKey(emailGuess)}`;
  }
  return `${formId}-${suffix.toLowerCase()}`;
}

/**
 * Firestore read order: normalized id first, then the decoded link id.
 */
export function assessmentDocumentIdLookupOrder(decodedId: string): string[] {
  const original = String(decodedId ?? "").trim();
  if (!original) {
    return [];
  }
  const normalized = normalizeAssessmentDocumentId(original);
  if (normalized === original) {
    return [original];
  }
  return [normalized, original];
}
